import { existsSync, readFileSync } from "node:fs";
import { join } from "node:path";
import { toAbsoluteCdnPath } from "./cdn-base.js";
import { resolveOpaquePdfPublicPath } from "./pdf-opaque-url.js";

const PDF_MANIFEST_PATH =
  typeof process !== "undefined" && typeof process.cwd === "function"
    ? join(process.cwd(), ".generated", "pdf-opaque-manifest.json")
    : "";

export const LEVELS = ["A1", "A2", "B1", "B2"];

/** @type {Record<string, string> | null} */
let PDF_OPAQUE_MAP = null;
/** @type {string[] | null} */
let PDF_OPAQUE_RESERVED_IDS = null;

function loadPdfManifest() {
  try {
    if (!PDF_MANIFEST_PATH) return { entries: {}, reservedIds: [] };
    if (!existsSync(PDF_MANIFEST_PATH)) return { entries: {}, reservedIds: [] };
    const raw = JSON.parse(readFileSync(PDF_MANIFEST_PATH, "utf8"));
    if (!raw || typeof raw !== "object") return { entries: {}, reservedIds: [] };
    const entries = raw.entries && typeof raw.entries === "object" ? raw.entries : {};
    const reservedIds = Array.isArray(raw.reservedIds)
      ? raw.reservedIds.filter((id) => typeof id === "string" && id.trim())
      : [];
    return { entries, reservedIds };
  } catch {
    return { entries: {}, reservedIds: [] };
  }
}

function getPdfOpaqueMap() {
  if (PDF_OPAQUE_MAP) return PDF_OPAQUE_MAP;
  const manifest = loadPdfManifest();
  PDF_OPAQUE_MAP = manifest.entries;
  PDF_OPAQUE_RESERVED_IDS = manifest.reservedIds;
  return PDF_OPAQUE_MAP;
}

function getPdfOpaqueReservedIds() {
  getPdfOpaqueMap();
  return PDF_OPAQUE_RESERVED_IDS || [];
}

function opaquePdfHrefForRelPath(relPath) {
  const opaqueMap = getPdfOpaqueMap();
  if (opaqueMap[relPath]) return opaqueMap[relPath];
  return resolveOpaquePdfPublicPath(relPath, getPdfOpaqueReservedIds());
}

function downloadCdnUrl(publicPath) {
  return toAbsoluteCdnPath(String(publicPath || "").trim());
}

/**
 * Level PDF bundle on CDN (stories, dialogs, or story solution keys).
 * @param {string} lang e.g. `de`, `ch`
 * @param {"geschichten" | "dialoge"} section
 * @param {string} level e.g. `A1`
 * @param {boolean} [solutions]
 */
export function overviewLevelPdfUrl(lang, section, level, solutions = false) {
  const code = String(lang || "").trim().toLowerCase();
  const sec = String(section || "").trim().toLowerCase();
  const lvl = String(level || "").trim().toLowerCase();
  if (!code || !sec || !lvl) return "";
  const fileName = solutions ? `${sec}-${lvl}~loesungen.pdf` : `${sec}-${lvl}.pdf`;
  return downloadCdnUrl(opaquePdfHrefForRelPath(`${code}/${sec}/${fileName}`));
}

/** Full PDF archive ZIP for a locale (stories, dialogs, etc.). */
export function overviewPdfZipUrl(lang) {
  const code = String(lang || "").trim().toLowerCase();
  if (!code) return "";
  return downloadCdnUrl(`/zip/${code}/pdf.zip`);
}

/** Full audio archive ZIP for a locale. */
export function overviewAudioZipUrl(lang) {
  const code = String(lang || "").trim().toLowerCase();
  if (!code) return "";
  return downloadCdnUrl(`/zip/${code}/audio.zip`);
}
